<!--
  AttachmentField — the grid renderer for file fields: `attachment` and its
  image / document / video subtypes, and the aggregate Attachments column (the
  record's files outside any field). In the row, thumbnails sized by the row
  height; selected, a "+" that opens the editor; editing, a wider panel to add
  from the library or the computer, and remove. Files dropped on the cell
  upload into it.

  Picks and removals edit the draft like any reference field and persist on
  commit. An upload can outlast its editor, so a finished one joins the draft
  while the editor is open, and is written through the cell's writer once it
  has closed.
-->
<template>
  <div v-if="mode === 'editing'" class="attachment-field is-editing">
    <ul v-if="assets.length" class="files">
      <li v-for="(asset, i) in assets" :key="asset.id" :title="asset.name ?? undefined">
        <button class="preview" @click.stop="view(i)">
          <FileThumb :asset="asset" fluid :compact="false" />
        </button>
        <span class="name is-elided text-7">{{ asset.name || "Untitled" }}</span>
        <button class="delete is-small" aria-label="Remove file" @click.stop="remove(asset)" />
      </li>
    </ul>
    <input
      ref="searchRef"
      v-model="query"
      class="input is-small"
      :placeholder="searchPlaceholder"
      @focus="searching = true"
    />
    <FileSearchResults
      v-if="searching"
      :query="query"
      :exclude="assetIds"
      :view="accepts"
      @pick="add"
    />
    <FileDropZone
      :target="uploadTarget"
      :multiple="field.isArray"
      :accepts="accepts"
      :disabled="!writable"
    />
  </div>

  <FileDropZone
    v-else
    class="attachment-field"
    :class="`is-${mode}`"
    :target="uploadTarget"
    :multiple="field.isArray"
    :accepts="accepts"
    :disabled="!writable"
    label="Drop to add"
  >
    <div class="thumbs">
      <span
        v-for="(asset, i) in assets"
        :key="asset.id"
        class="preview"
        :title="asset.name ?? undefined"
        @click="onPreviewClick($event, i)"
      >
        <FileThumb :asset="asset" fluid :compact="false" />
        <button
          v-if="mode === 'active' && writable"
          class="delete is-small"
          aria-label="Remove file"
          @click.stop="detach(asset)"
        />
      </span>
      <span v-if="mode === 'active' && writable" class="add" title="Add files">
        <font-awesome-icon icon="plus" />
      </span>
    </div>
  </FileDropZone>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from "vue"
import { type Field, getFieldType, isReferenceDef } from "@auger/core/fields"
import type { AssetFieldsFragment, BaseRecordFragment } from "@auger/core/gql/types"
import { CELL_CONTEXT } from "@auger/ui/composables/useInteraction"
import { type FieldMode, type FieldSize } from "@auger/ui/fields/useField"
import FileDropZone from "@/components/files/FileDropZone.vue"
import FileSearchResults from "@/components/files/FileSearchResults.vue"
import FileThumb from "@/components/files/FileThumb.vue"
import { useFileViewer } from "@/composables/useFileViewer"
import type { UploadTarget } from "@/stores/uploads"

type Asset = Pick<AssetFieldsFragment, "id" | "name" | "mime_type" | "url"> &
  Partial<Pick<AssetFieldsFragment, "size" | "metadata">>

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const cell = inject(CELL_CONTEXT, null)

// The value as entities. Orphans (asset gone) arrive as `{ id }` and show as a
// blank tile; anything that isn't an entity is ignored.
const assets = computed(() =>
  (props.modelValue == null
    ? []
    : Array.isArray(props.modelValue)
      ? props.modelValue
      : [props.modelValue]
  ).filter((a): a is Asset => !!a && typeof a === "object" && "id" in a)
)
const assetIds = computed(() => assets.value.map((a) => Number(a.id)))

const record = computed(() => {
  const item = cell?.item()
  return (item?.record ?? item ?? null) as BaseRecordFragment | null
})

// The one kind of file the field holds (an audio field, …); none takes any file.
const accepts = computed(() => {
  const def = getFieldType(props.field.type)
  return isReferenceDef(def) ? def.accepts : undefined
})

const searchPlaceholder = computed(() => {
  const what = accepts.value && accepts.value !== "document" ? `${accepts.value} ` : ""
  const verb = props.field.isArray || !assets.value.length ? "Add" : "Replace with"
  return `${verb} ${what}from library…`
})

const writable = computed(() => !!props.field.isEditable && !!cell?.writer())

// --- Draft edits ----------------------------------------------------------------

function setFiles(next: Asset[]) {
  emit("update:modelValue", props.field.isArray ? next : (next.at(-1) ?? null))
}

function add(asset: Asset) {
  if (assetIds.value.includes(Number(asset.id))) return
  setFiles([...assets.value, asset])
  query.value = ""
  searching.value = false
}

function remove(asset: Asset) {
  setFiles(assets.value.filter((a) => a.id !== asset.id))
}

/** Remove a file from a selected cell straight away; the editor's removals
 *  wait for its commit instead. */
function detach(asset: Asset) {
  cell?.writer()?.({ remove: [asset] })
}

// --- Uploads --------------------------------------------------------------------

// Whether this editor still holds the draft. Once it closes, the draft has been
// committed or discarded, so a late upload writes on its own.
const editorOpen = ref(props.mode === "editing")
watch(
  () => props.mode,
  (mode) => (editorOpen.value = mode === "editing")
)
onBeforeUnmount(() => (editorOpen.value = false))

const uploadTarget = computed<UploadTarget>(() => {
  const r = record.value
  const write = cell?.writer() ?? null
  return {
    record: r ? { id: r.id, type: r.type } : null,
    destination: `${r?.name ?? "Record"} · ${props.field.label || props.field.key}`,
    onUploaded: (asset) =>
      editorOpen.value ? add(asset as Asset) : write?.({ add: [asset as Asset] }),
  }
})

// --- Viewing and the library search ---------------------------------------------

const viewer = useFileViewer()
function view(index: number) {
  const files = assets.value.filter((a) => a.url)
  const at = files.findIndex((a) => a.id === assets.value[index]?.id)
  if (at >= 0) viewer.open(files as never, at, record.value?.name ?? null)
}

// In the row a click selects the cell; once it's selected, a click opens the file.
function onPreviewClick(event: MouseEvent, index: number) {
  if (props.mode !== "active") return
  event.stopPropagation()
  view(index)
}

const query = ref("")
const searching = ref(false)
const searchRef = ref<HTMLInputElement | null>(null)
watch(
  () => props.mode === "editing",
  (on) => {
    if (on) nextTick(() => searchRef.value?.focus())
  },
  { immediate: true }
)
</script>

<style>
/* Thumbnails are squares sized by the row-height class: the grid carries it,
   and so does a cell's teleported editor. Medium and Tall lay out the same,
   at two sizes. */
.attachment-field {
  --thumb: 22px;

  display: flex;
  width: 100%;
  min-width: 0;

  .thumbs {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    width: 100%;
    min-width: 0;
    min-height: var(--thumb);
  }

  .is-medium-rows & {
    --thumb: 80px;
  }

  .is-tall-rows & {
    --thumb: 176px;
  }

  .preview,
  .add {
    flex: none;
    display: flex;
    width: var(--thumb);
    height: var(--thumb);
  }

  /* The "+" keeps the short-row size whatever the row height. */
  .add {
    width: 22px;
    height: 22px;
  }

  .file-thumb {
    width: 100%;
    height: 100%;
    border-radius: var(--auger-radius-sm);
  }

  /* A 22px tile has room for the kind's icon, not its badge. */
  .is-short-rows & .file-thumb {
    .ext {
      display: none;
    }

    &.checkerboard {
      background-size: 6px 6px;
    }
  }

  &.is-active .preview {
    position: relative;
    cursor: zoom-in;

    /* Inside the tile's corner, clear of the "+" beside it. */
    .delete {
      position: absolute;
      top: 2px;
      right: 2px;
      opacity: 0;
    }

    &:hover .delete {
      opacity: 1;
    }
  }

  /* A 22px tile is too small to share with a remove button; short rows remove
     in the editor. */
  .is-short-rows &.is-active .preview .delete {
    display: none;
  }

  /* Selected, the cell shows every file rather than what fits the row. */
  &.is-active .thumbs {
    flex-wrap: wrap;
  }

  .add {
    margin-left: auto;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--auger-border-hover);
    border-radius: var(--auger-radius-sm);
    color: var(--auger-text-weak);
    cursor: pointer;

    &:hover {
      border-color: var(--auger-link);
      color: var(--auger-link);
    }
  }

  &.file-drop-zone > .overlay {
    font-size: var(--auger-size-7);
    border-width: 1px;
  }

  /* Editing gets room: named tiles, a library search and a drop target. */
  &.is-editing {
    --thumb: 72px;

    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    padding: 0.25rem;

    .files {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        width: var(--thumb);
      }

      .preview {
        padding: 0;
        border: 0;
        background: none;
        cursor: zoom-in;
      }

      .name {
        text-align: center;
      }

      .delete {
        position: absolute;
        top: -5px;
        right: -5px;
      }
    }

    .file-search-results {
      max-height: 16rem;
      overflow-y: auto;
    }
  }
}

/* The editor's box widens to hold the panel, and on a column near the right
   edge it aligns to the cell's right side instead of running off. */
.grid-editor:has(> .attachment-field.is-editing) {
  width: 360px;
  max-width: 480px;
  position-try-fallbacks:
    flip-inline,
    flip-block,
    flip-block flip-inline;
}
</style>
